import { wait } from './wait.js'
// using reduce to pipe functions together
// expects an array of generator functions
const pipeGenerators = fns => x => {
	if (fns && fns.length > 0) {
		return fns.reduce((v, fn) => fn(v), x);
	} else {
		return x;
	}
};

// using reduce to pipe functions together by wrapping them with async generator functions
// expects an array of functions
const pipe = fns => x => {
	if (fns && fns.length > 0) {
		return fns.reduce((v, fn) => {
			return (async function* (input) {
				for await (const i of input) {
					yield fn(i);
				}
			})(v);
		}, x);
	} else {
		return x;
	}
};

/**
 * async generator will produce the entries in the queue one by one and wait for new entries
 * if onlyLast === true the async generator will produce only the final entry in the queue
 * will skip entries if new entries come in within the given ms (defaults to 500)
 *
 * @param {boolean} onlyLast	if true only produce last entry, produce new entry if added within (ms)milliseconds 
 * 									defaults to false
 * @param {Array}	queue		array to hold the actions that will be produced
 * @param {Object}	ref			reference object to hold the callback function used to resolve a promise
 * @param {number}	ms			number of milliseconds to wait for new entries before producing them
 * 									defaults to 500
 * @param {number}	pause		number of milliseconds to pause before checking for new entries to produce
 * 									defaults to 0
 */
async function* produce({ onlyLast = false, queue, ref, ms = 500, pause = 0 } = {}) {
	for (;;) {
		while (queue.length) {
			if (onlyLast) {
				if (queue.length > 1) {
					queue.shift();
					continue;
				}
				if (ms > 0) {
					await wait(ms);
				}
				if (queue.length > 1) {
					continue;
				}
			}
			yield queue.shift(); //yield the first entry in the queue.
			if (pause > 0) {
				await wait(pause);
			}
		}
		await new Promise(i => (ref.callback = i));
		ref.callback = null;
	}
}

/**
 * async function to consume the entries produced and sending them down the pipeline
 *
 * @param {*}			producer	iterable or generator function that produces actions to be consumed
 * @param {function}	finalFn		Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {Array}		fns			Array of functions to let each action pass through before being handled by the finalFn
 * @param {boolean}		gen			if true the functions are generator functions defaults to false
 */
async function consume(producer, finalFn = () => {}, fns, gen = false) {
	let pipeline = gen ? pipeGenerators(fns) : pipe(fns);
	for await (const i of pipeline(producer)) {
		finalFn(i); // anything added to the queue eventually ends up here.
	}
}

/**
 * get a producer object that will allow iteration to produce the entries in the queue one by one and wait for new entries
 * if onlyLast === true the asynciterator will produce only the final entry in the queue
 * will skip entries if new entries come in within the given ms (defaults to 500)
 *
 * @param {boolean}	onlyLast	if true only produce last entry, produce new entry if added within (ms)milliseconds 
 * 									defaults to false
 * @param {number}	ms			number of milliseconds to wait for new entries before producing them
 * 									defaults to 500
 * @param {number}	pause		number of milliseconds to pause before checking for new entries to produce
 * 									defaults to 0
 * @returns {*} iterable object
 */
function getProducer({onlyLast = false, ms = 500, pause = 0} = {}) {
	return {
		callback: null,
		queue: [],
		[Symbol.asyncIterator]: async function* () {
			for (;;) {
				while (this.queue.length) {
					if (onlyLast) {
						if (this.queue.length > 1) {
							this.queue = [this.queue.pop()];
							continue;
						}
						if (ms > 0) {
							await wait(ms);
						}
						if (this.queue.length > 1) {
							continue;
						}
					}
					yield this.queue.shift();
					if (pause > 0) {
						await wait(pause);
					}
				}
				await new Promise(i => (this.callback = i));
				this.callback = null;
			}
		}
	};
}

/**
 * get a producer object that will allow iteration to produce the entries in the queue one by one and wait for new entries
 * if onlyFirst === true the asynciterator will produce only the first entry in the queue
 * will skip new entries when they come in within the given ms (defaults to 200)
 *
 * @param {boolean}	onlyFirst	if true only produce first entry, skip new entry if added within (ms)milliseconds 
 * 									defaults to false
 * @param {number}	ms			number of milliseconds to wait for new entries before producing them
 * 									defaults to 200
 * @returns {*} iterable object
 */
function getReducer({onlyFirst = false, now = false, ms = 200}) {
	return {
		callback: null,
		queue: [],
		[Symbol.asyncIterator]: async function* () {
			for (;;) {
				while (this.queue.length) {
					if (onlyFirst && !now) {
						if (ms > 0) {
							await wait(ms);
						}
						if (this.queue.length > 1) {
							this.queue = [this.queue.shift()];
							continue;
						}
					}
					yield this.queue.shift();
					if (onlyFirst && now) {
						if (ms > 0) {
							await wait(ms);
						}
						if (this.queue.length > 1) {
							this.queue = [this.queue.shift()];
							continue;
						}
					}
				}
				await new Promise(i => (this.callback = i));
				this.callback = null;
			}
		}
	};
}

/**
 * Process all actions in a queue
 * Use it to process al the actions in a queue
 * pass regular functions as parameters
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		pause	number of milliseconds to pause before checking for new entries to produce
 * @param {Array}		fns 	Array of functions to let each action pass through before being handled by the finalFn
 * @returns {*}					An object you can push the actions you need processed to
 */ 
function processQueue(finalFn, pause, ...fns) {
	const ref = {
		callback: null
	};	
	const queue = [];

	const producer = produce({
		queue,
		ref,
		pause
	});	

	consume(producer, finalFn, fns);

	return {
		push(action) {
			if (ref.callback) {
				ref.callback();
			}	
			queue.push(action);
		}	
	};	
}	

/**
 * Process first action in a queue added within a .2 second timeframe
 * Use it to process events where maybe some extra event get fired in a queue
 * pass regular functions as parameters
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		ms		number of milliseconds to wait for new entries before producing them
 * @param {Array}		fns 	Array of functions to let each action pass through before being handled by the finalFn
 * @returns {*}			An object you can push the actions you need processed to
 */
function processFirst(finalFn, ms, ...fns) {
	const gen = getReducer({onlyFirst: true, ms});
	consume(gen, finalFn, fns);
	return {
		push(val) {
			if (gen.callback) {
				gen.callback();
			}
			gen.queue.push(val);
		}
	};
}

/**
 * Process first action in a queue immediately and wait for a .2 second timeframe before processing the first event added to the queue
 * Use it to process events where you need to throttle events fired in a queue
 * pass regular functions as parameters
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		ms		number of milliseconds to wait for new entries before producing them
 * @param {Array}		fns 	Array of functions to let each action pass through before being handled by the finalFn
 * @returns {*}			An object you can push the actions you need processed to
 */
function processFirstNow(finalFn, ms, ...fns) {
	const gen = getReducer({onlyFirst: true, now: true, ms});
	consume(gen, finalFn, fns);
	return {
		push(val) {
			if (gen.callback) {
				gen.callback();
			}
			gen.queue.push(val);
		}
	};
}

/**
 * Process last action in a queue added within a .5 second timeframe
 * Use it to process events where maybe somebody changes their mind the actions in a queue
 * pass regular functions as parameters
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		ms		number of milliseconds to wait for new entries before producing them
 * @param {Array}		fns 	Array of functions to let each action pass through before being handled by the finalFn
 * @returns {*}			An object you can push the actions you need processed to
 */
function processEvent(finalFn, ms, ...fns) {
	const por = getProducer({onlyLast: true, ms});
	consume(por, finalFn, fns);
	return {
		push(val) {
			if (por.callback) {
				por.callback();
			}
			por.queue.push(val);
		}
	};
}

/**
 * Process all actions in a queue
 * Use it to process al the actions in a queue
 * pass generator functions as parameters apart from the finalFn
 * generator functions will give you more control but are harder to write
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		pause	number of milliseconds to pause before checking for new entries to produce
 * @param {Array}		fns 	Array of generator functions to let each action pass through before being handled by the finalFn
 * @returns {*}			An object you can push the actions you need processed to
 */
function processQueueAsync(finalFn, pause, ...fns) {
	const producer = getProducer({pause});
	consume(producer, finalFn, fns, true);
	return {
		push(action) {
			if (producer.callback) {
				producer.callback();
			}
			producer.queue.push(action);
		}
	};
}

/**
 * Process last action in a queue added within a .5second timeframe
 * Use it to process events where maybe somebody changes their mindal the actions in a queue
 * pass generator functions as parameters apart from the finalFn
 * generator functions will give you more control but are harder to write
 *
 * @param {function}	finalFn	Function to execute for every action in the queue, will be called at the end of the pipeline
 * @param {number}		ms		number of milliseconds to wait for new entries before producing them
 * @param {Array}		fns 	Array of generator functions to let each action pass through before being handled by the finalFn
 * @returns {*}			An object you can push the actions you need processed to
 */
function processEventAsync(finalFn, ms, ...fns) {
	const gener = getProducer({onlyLast: true, ms});
	consume(gener, finalFn, fns, true);
	return {
		push(action) {
			if (gener.callback) {
				gener.callback();
			}
			gener.queue.push(action);
		}
	};
}

function debounce(fn, ms) {
	if (!fn || typeof fn !== 'function') {
		return;
	}
	const eventQueue = processEvent(fn, ms);
	return function(args) {
		eventQueue.push(args);
	}
}

function throttle(fn, ms) {
	if (!fn || typeof fn !== 'function') {
		return;
	}
	const eventQueue = processFirstNow(fn, ms);
	return function(args) {
		eventQueue.push(args);
	}
}

export {
	debounce,
	throttle,
	processQueue,
	processEvent,
	processFirst,
	processFirstNow,
	processQueueAsync,
	processEventAsync
};